import { createContext, useContext, useEffect, useMemo, useState } from "react";

const CartContext = createContext(null);
const KEY = "lg_cart_v2";

export function CartProvider({ children }) {
  const [items, setItems] = useState(() => {
    try {
      const raw = JSON.parse(localStorage.getItem(KEY) || "[]");
      // Defensive: drop any legacy book line items — books are sold on Amazon now.
      return raw.filter((x) => x.kind !== "product" || x.type !== "book");
    } catch { return []; }
  });

  useEffect(() => { localStorage.setItem(KEY, JSON.stringify(items)); }, [items]);

  const addItem = (product) => {
    setItems((prev) => {
      if (prev.find((p) => p.kind === "product" && p.product_id === product.product_id)) return prev;
      return [...prev, {
        kind: "product",
        product_id: product.product_id,
        title: product.title,
        price: Number(product.price),
        type: product.type,
        cover_image: product.cover_image,
        author: product.author,
        instructor: product.instructor,
        quantity: 1,
      }];
    });
  };

  const addBundle = (bundle) => {
    setItems((prev) => {
      if (prev.find((p) => p.kind === "bundle" && p.bundle_id === bundle.bundle_id)) return prev;
      return [...prev, {
        kind: "bundle",
        bundle_id: bundle.bundle_id,
        slug: bundle.slug,
        title: bundle.title,
        subtitle: bundle.subtitle,
        price: Number(bundle.bundle_price),
        list_total: Number(bundle.list_total),
        savings: Number(bundle.savings),
        cover_image: bundle.cover_image,
        item_count: bundle.products?.length || 0,
        quantity: 1,
      }];
    });
  };

  const removeItem = (key) =>
    setItems((p) => p.filter((x) => (x.kind === "bundle" ? x.bundle_id !== key : x.product_id !== key)));

  const clear = () => setItems([]);
  const has = (product_id) => items.some((x) => x.kind === "product" && x.product_id === product_id);
  const hasBundle = (bundle_id) => items.some((x) => x.kind === "bundle" && x.bundle_id === bundle_id);

  const total = useMemo(
    () => items.reduce((s, x) => s + Number(x.price) * (x.quantity || 1), 0),
    [items],
  );

  const value = useMemo(
    () => ({ items, addItem, addBundle, removeItem, clear, has, hasBundle, total }),
    [items, total],
  );

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}

export const useCart = () => useContext(CartContext);
